import { getApiUrl } from "../api";
import { useEffect, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import { getCloudflareImageUrl } from "../utils/cloudflareImages";
import { ArabicFooter, ArabicHeader } from "./ArabicHome";


const stripHtml = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const getCurrentLanguage = () => {
  const path = window.location.pathname.toLowerCase();
  const htmlLang = document.documentElement.lang?.toLowerCase() || "";
  return path.startsWith("/ar/") || htmlLang.startsWith("ar") ? "ar" : "en";
};

const labels = {
  en: {
    pageTitle: "Contact Us",
    home: "home",
    badge: "Contact Us",
    heading: "Get in Touch",
    description:
      "Our employees work around the clock to receive your honor, and they are also fully prepared to answer your inquiries and receive your suggestions through the website or phone number",
    contactInfo: "Contact Info:",
    formBadge: "Send Message",
    formHeading: "Write to Us",
    formDescription: "Fill out the form below and our team will respond to your inquiry as soon as possible.",
    firstName: "Enter First Name *",
    lastName: "Enter Last Name *",
    phone: "Enter Phone Number *",
    email: "Enter Email Address *",
    message: "Any Message...",
    submit: "Submit Message",
    sending: "Sending...",
    note: "Please make sure your contact details are correct so our team can reach you easily.",
    location: "Our Location",
    success: "Thank you. Your message has been sent successfully.",
    required: "Please complete all required fields.",
    failed: "Unable to send your message right now. Please try again later.",
  },
  ar: {
    pageTitle: "Contact Us",
    home: "home",
    badge: "Contact Us",
    heading: "Get in Touch",
    description:
      "Our employees work around the clock to receive your honor, and they are also fully prepared to answer your inquiries and receive your suggestions through the website or phone number",
    contactInfo: "Contact Info:",
    formBadge: "Send Message",
    formHeading: "Write to Us",
    formDescription: "Fill out the form below and our team will respond to your inquiry as soon as possible.",
    firstName: "Enter First Name *",
    lastName: "Enter Last Name *",
    phone: "Enter Phone Number *",
    email: "Enter Email Address *",
    message: "Any Message...",
    submit: "Submit Message",
    sending: "Sending...",
    note: "Please make sure your contact details are correct so our team can reach you easily.",
    location: "Our Location",
    success: "Thank you. Your message has been sent successfully.",
    required: "Please complete all required fields.",
    failed: "Unable to send your message right now. Please try again later.",
  },
};

const visibleContactBoxes = [
  { number: 2, defaultIcon: "fa-envelope" },
  { number: 3, defaultIcon: "fa-location-dot" },
];

const extractMapSrc = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const srcMatch = raw.match(/\ssrc=["']([^"']+)["']/i);
  return srcMatch ? srcMatch[1] : raw;
};

const buildContactContent = (item, language) => {
  const source = item || {};
  const read = (field, fallback = "") => stripHtml(source[`${field}_${language}`] || source[`${field}_en`] || fallback);

  return {
    title: read("title", labels[language].heading),
    description: read("description", labels[language].description),
    mapSrc: extractMapSrc(source.map_embed),
    items: visibleContactBoxes.map(({ number, defaultIcon }) => ({
      id: `${source.id || "contact"}-${number}`,
      title: read(`box${number}title`),
      value: read(`box${number}description`),
      icon: source[`box${number}icon`] || "",
      defaultIcon,
    })).filter((item) => item.title || item.value),
  };
};

function ContactIcon({ icon, defaultIcon }) {
  const image = getCloudflareImageUrl(icon, "w=120,sharpen=1");
  if (image) return <img className="contact-api-icon" src={image} alt="" />;
  return <i className={`fa-solid ${defaultIcon}`}></i>;
}

const getContactHref = (item) => {
  const value = item.value || "";
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return `mailto:${value}`;
  if (/^[+()0-9\s-]+$/.test(value)) return `tel:${value.replace(/[^\d+]/g, "")}`;
  return "";
};

const initialForm = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  message: "",
};

export default function ContactUs({ language: routeLanguage }) {
  const interactions = useHomeInteractions();
  const language = routeLanguage || getCurrentLanguage();
  const isArabic = language === "ar";
  const text = isArabic ? {
    ...labels.ar,
    pageTitle: "اتصل بنا", home: "الرئيسية", badge: "اتصل بنا", heading: "تواصل معنا",
    description: "يعمل موظفونا على مدار الساعة لاستقبال استفساراتكم ومقترحاتكم عبر الموقع الإلكتروني أو الهاتف.",
    contactInfo: "معلومات التواصل:", formBadge: "إرسال رسالة", formHeading: "اكتب إلينا",
    formDescription: "املأ النموذج أدناه وسيرد فريقنا على استفسارك في أقرب وقت ممكن.",
    firstName: "أدخل الاسم الأول *", lastName: "أدخل اسم العائلة *", phone: "أدخل رقم الهاتف *", email: "أدخل البريد الإلكتروني *", message: "اكتب رسالتك...",
    submit: "إرسال الرسالة", sending: "جارٍ الإرسال...", note: "يرجى التأكد من صحة بيانات التواصل حتى يتمكن فريقنا من الوصول إليك بسهولة.",
    location: "موقعنا", success: "شكرًا لك. تم إرسال رسالتك بنجاح.", required: "يرجى إكمال جميع الحقول المطلوبة.", failed: "تعذر إرسال رسالتك حالياً. يرجى المحاولة لاحقاً.",
  } : labels.en;
  const [content, setContent] = useState({ title: "", description: "", mapSrc: "", items: [] });
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    document.title = `${text.pageTitle} | ${isArabic ? "مؤسسة تراحم الخيرية" : "Tarahum Charity Foundation"}`;
    if (!isArabic) return undefined;
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");
    return () => {
      document.documentElement.lang = "en";
      document.documentElement.dir = "ltr";
      document.body.classList.remove("arabic-layout-page");
    };
  }, [isArabic, text.pageTitle]);

  useEffect(() => {
    const controller = new AbortController();

    const loadContact = async () => {
      try {
        const response = await fetch(getApiUrl("/api/GetContactUsList.ashx?page=1&pageSize=1"), {
          headers: { Accept: "application/json" },
          credentials: "include",
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`Contact request failed with ${response.status}`);

        const result = await response.json();
        const rows = Array.isArray(result?.data) ? result.data : [];
        setContent(rows[0] ? buildContactContent(rows[0], language) : { title: "", description: "", mapSrc: "", items: [] });
      } catch (error) {
        if (error.name !== "AbortError") {
          console.warn("Unable to load contact content", error);
          setContent({ title: "", description: "", mapSrc: "", items: [] });
        }
      }
    };

    loadContact();
    return () => controller.abort();
  }, [language]);

  const updateForm = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: "", message: "" });

    if (!form.firstName.trim() || !form.lastName.trim() || !form.phone.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ type: "error", message: text.required });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(getApiUrl("/api/SendContactUs.ashx"), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          ...form,
          pageUrl: window.location.href,
        }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false || result.status === "error") {
        throw new Error(result.message || `Contact form failed with ${response.status}`);
      }

      setForm(initialForm);
      setStatus({ type: "success", message: result.message || text.success });
    } catch (error) {
      console.warn("Unable to submit contact form", error);
      setStatus({ type: "error", message: text.failed });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Preloader />
      {isArabic ? <ArabicHeader /> : <><Header /><LanguagePopup /></>}

      <section className="kid-d-wrp pb-0">
        <div className="kid-container">
          <div className="kid-hero">
            <h1>{text.pageTitle}</h1>
            <ul className="kid-breadcrumb">
              <li><Link to="/">{text.home}</Link></li>
              <li>/</li>
              <li>{text.pageTitle}</li>
            </ul>
          </div>
        </div>
      </section>

      <section className={`contact-wrap${isArabic ? " arabic-contact-page" : ""}`} dir={isArabic ? "rtl" : undefined}>
        <div className="container">
          <div className="contact-container">
            <div className="contact-box">
              <div className="contact-info-panel">
                <div className="contact-badge">
                  <span></span>
                  {text.badge}
                </div>

                <h2>{content.title}</h2>
                <p className="contact-desc">{content.description}</p>
                <h3 className="contact-sub-title">{text.contactInfo}</h3>

                <div className="contact-list">
                  {content.items.map((item) => {
                    const href = getContactHref(item);
                    return (
                      <div className="contact-item" key={item.id}>
                        <div className="contact-icon">
                          <ContactIcon icon={item.icon} defaultIcon={item.defaultIcon} />
                        </div>
                        <div>
                          <h3>{item.title}</h3>
                          {href ? <a href={href}>{item.value}</a> : <p>{item.value}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="contact-form-panel">
                <div className="contact-badge">
                  <span></span>
                  {text.formBadge}
                </div>

                <h2>{text.formHeading}</h2>
                <p className="contact-desc">{text.formDescription}</p>

                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="contact-form-grid">
                    <div className="contact-field">
                      <input type="text" placeholder={text.firstName} value={form.firstName} onChange={updateForm("firstName")} />
                    </div>

                    <div className="contact-field">
                      <input type="text" placeholder={text.lastName} value={form.lastName} onChange={updateForm("lastName")} />
                    </div>

                    <div className="contact-field">
                      <input type="tel" placeholder={text.phone} value={form.phone} onChange={updateForm("phone")} />
                    </div>

                    <div className="contact-field">
                      <input type="email" placeholder={text.email} value={form.email} onChange={updateForm("email")} />
                    </div>
                  </div>

                  <div className="contact-field">
                    <textarea placeholder={text.message} value={form.message} onChange={updateForm("message")}></textarea>
                  </div>

                  {status.message ? (
                    <div className={`contact-form-message ${status.type === "success" ? "is-success" : "is-error"}`}>
                      {status.message}
                    </div>
                  ) : null}

                  <button type="submit" className="contact-submit" disabled={isSubmitting}>
                    {isSubmitting ? text.sending : text.submit}
                  </button>
                </form>

                <div className="contact-note">
                  <i className="fa-solid fa-circle-info"></i>
                  {text.note}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className={`google-map${isArabic ? " arabic-contact-page" : ""}`} dir={isArabic ? "rtl" : undefined}>
        <div className="container">
          <div className="row section-row">
            <div className="col-lg-12">
              <div className="section-title section-title-center">
                <span className="section-sub-title wow fadeInUp">{text.location}</span>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-12">
              <div className="google-map-iframe wow fadeInUp" data-wow-delay="0.4s">
                <iframe
                  src={content.mapSrc}
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={isArabic ? "موقع مؤسسة تراحم الخيرية" : "Tarahum Charity Foundation location"}
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isArabic ? <ArabicFooter /> : <Footer />}
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
